/**
 * Defensive JSON parser for revision-history payloads, used under both editor
 * engines. Handles the edge cases that otherwise surface as
 * "undefined is not valid JSON".
 */

/**
 * Safely parse JSON string with comprehensive edge case handling
 * @param str - The string to parse (or undefined/null)
 * @param fallback - Value to return if parsing fails or input is invalid
 * @returns Parsed object or fallback value
 */
export function safeParseJson<T = any>(str: string | undefined | null, fallback: T): T {
  // Callers pass through every variant of undefined/null/empty, each of which throws in JSON.parse.
  if (
    str === 'undefined' ||
    str === '"undefined"' ||
    str === "'undefined'" ||
    str === undefined ||
    str === null ||
    str === '' ||
    str === 'null' ||
    str === '"null"' ||
    str === "'null'" ||
    str === 'NULL' ||
    str === 'Null'
  ) {
    return fallback;
  }

  // Handle whitespace-only strings
  if (typeof str === 'string' && str.trim() === '') {
    return fallback;
  }

  // Handle already parsed objects (no need to parse)
  if (typeof str === 'object' && str !== null) {
    return str as T;
  }

  // Only attempt parsing on non-empty strings
  if (typeof str !== 'string' || str.length === 0) {
    return fallback;
  }

  // Check if string looks like it might be "undefined" wrapped in quotes
  if (str.indexOf('undefined') !== -1 && str.length < 20) {
    // Double-check it's not valid JSON that happens to contain "undefined"
    if (!str.startsWith('{') && !str.startsWith('[')) {
      console.warn(`safeParseJson: Suspicious string containing "undefined": "${str}", returning fallback`);
      return fallback;
    }
  }

  try {
    return JSON.parse(str) as T;
  } catch (error) {
    // Log only in development to help debug issues
    if (import.meta.env.MODE === 'development') {
      console.warn('safeParseJson: Failed to parse JSON:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        input: str.substring(0, 100) + (str.length > 100 ? '...' : ''),
        fallback,
      });
    }
    return fallback;
  }
}

/**
 * Normalize diffData to CKEditor's expected format: object with string values
 * Each root must be a stringified JSON object
 * @param raw - Raw diffData from backend
 * @returns Normalized diffData or undefined if invalid
 */
export function normalizeDiffData(raw: any): Record<string, string> | undefined {
  if (!raw || raw === 'undefined' || raw === 'null') {
    return undefined;
  }

  try {
    // Parse if it's a JSON string
    const parsed = typeof raw === 'string' ? safeParseJson(raw, undefined) : raw;
    if (!parsed || typeof parsed !== 'object') {
      return undefined;
    }

    // Convert each root to string format as CKEditor expects
    const normalized: Record<string, string> = {};
    for (const root of Object.keys(parsed)) {
      const rootData = parsed[root];

      // A literal "undefined" string in a root would throw downstream.
      if (
        rootData === 'undefined' ||
        rootData === '"undefined"' ||
        rootData === "'undefined'" ||
        rootData === 'null' ||
        rootData === '"null"'
      ) {
        // Provide valid empty diff instead of problematic strings
        normalized[root] = JSON.stringify({ insertions: [], deletions: [] });
      } else if (typeof rootData === 'string') {
        // Check if it's double-stringified and fix it
        const maybeObj = safeParseJson(rootData, null);
        if (typeof maybeObj === 'object' && maybeObj !== null) {
          normalized[root] = JSON.stringify(maybeObj);
        } else if (rootData.startsWith('{') || rootData.startsWith('[')) {
          // Looks like JSON but couldn't parse - don't pass through
          console.warn(`normalizeDiffData: Invalid JSON string in root "${root}", using empty diff`);
          normalized[root] = JSON.stringify({ insertions: [], deletions: [] });
        } else {
          // Plain string that's not JSON - might be valid diff data
          normalized[root] = rootData;
        }
      } else if (rootData && typeof rootData === 'object') {
        // Convert object to JSON string
        normalized[root] = JSON.stringify(rootData);
      } else {
        // Fallback for missing/invalid data
        normalized[root] = JSON.stringify({ insertions: [], deletions: [] });
      }
    }

    // Ensure "main" root exists - CKEditor expects this
    if (normalized && !normalized.main) {
      const firstRoot = Object.keys(normalized)[0];
      if (firstRoot && firstRoot !== 'main') {
        // Rename any non-main root to main
        normalized.main = normalized[firstRoot];
        delete normalized[firstRoot];
      } else if (!firstRoot) {
        // No roots at all, provide default
        normalized.main = JSON.stringify({ insertions: [], deletions: [] });
      }
    }

    return Object.keys(normalized).length > 0 ? normalized : undefined;
  } catch (error) {
    if (import.meta.env.MODE === 'development') {
      console.warn('normalizeDiffData: Failed to normalize:', error, raw);
    }
    return undefined;
  }
}

/**
 * Normalize attributes to object format (CKEditor expects Record<string, unknown>)
 * @param raw - Raw attributes from backend
 * @returns Normalized attributes object
 */
export function normalizeAttributes(raw: any): Record<string, unknown> {
  if (!raw || raw === 'undefined' || raw === 'null') {
    return {};
  }

  try {
    if (typeof raw === 'string') {
      // Parse JSON string to object using safe parser
      return safeParseJson(raw, {});
    } else if (typeof raw === 'object' && raw !== null) {
      return raw;
    }
  } catch (error) {
    if (import.meta.env.MODE === 'development') {
      console.warn('normalizeAttributes: Failed to normalize:', error, raw);
    }
  }

  return {};
}

/**
 * Validate that a revision has renderable content
 * A revision must have either data (snapshot) or diffData (diffs) to display
 * @param revision - The revision to validate
 * @returns true if revision has content, false if it will render blank
 */
export function validateRevisionContent(revision: { id?: string; data?: any; diffData?: any }): boolean {
  const hasData = revision.data !== undefined && revision.data !== null;
  const hasDiffData = revision.diffData !== undefined && revision.diffData !== null;

  if (!hasData && !hasDiffData) {
    if (import.meta.env.MODE === 'development') {
      console.warn(`Revision ${revision.id || 'unknown'} has no renderable content (missing both data and diffData)`);
    }
    return false;
  }

  return true;
}

/**
 * Extract HTML content from composite data structure
 * Handles both v41 raw HTML format and new { content: html } format
 * @param revisionData - The revision data to extract from
 * @returns HTML string or undefined
 */
export function extractRevisionContent(revisionData: any): string | undefined {
  // Invalid data arrives in several shapes; all must be treated as empty.
  if (
    !revisionData ||
    revisionData === 'undefined' ||
    revisionData === '"undefined"' ||
    revisionData === "'undefined'" ||
    revisionData === 'null' ||
    revisionData === '"null"' ||
    revisionData === ''
  ) {
    return undefined;
  }

  // If it's already a string starting with HTML, it's v41 format - return as-is
  if (typeof revisionData === 'string' && revisionData.startsWith('<')) {
    return revisionData;
  }

  // If it's a string, try to parse it
  if (typeof revisionData === 'string') {
    const parsed = safeParseJson(revisionData, undefined);
    if (parsed && typeof parsed === 'object' && parsed.content) {
      // Ensure content is not "undefined" string
      if (
        parsed.content === 'undefined' ||
        parsed.content === '"undefined"' ||
        parsed.content === "'undefined'" ||
        parsed.content === 'null' ||
        parsed.content === '"null"'
      ) {
        return undefined;
      }
      return parsed.content;
    }
    return undefined;
  }

  // If it's an object with content property, extract it
  if (revisionData && typeof revisionData === 'object' && revisionData.content) {
    // Ensure content is not "undefined" string
    if (
      revisionData.content === 'undefined' ||
      revisionData.content === '"undefined"' ||
      revisionData.content === "'undefined'" ||
      revisionData.content === 'null' ||
      revisionData.content === '"null"'
    ) {
      return undefined;
    }
    return revisionData.content;
  }

  return undefined;
}

/**
 * Safely convert a value to HTML string for revision viewing
 * Guards against JSON parsing HTML and handles diff-only revisions
 * @param maybe - The value that might be HTML string
 * @returns HTML string or undefined for diff-only/invalid data
 */
export function asHtmlString(maybe: unknown): string | undefined {
  if (typeof maybe !== 'string') return undefined;

  const s = maybe.trim();

  // Treat suspicious literals as missing - don't try to parse them as JSON
  if (
    !s ||
    s === 'undefined' ||
    s === '"undefined"' ||
    s === "'undefined'" ||
    s === 'null' ||
    s === '"null"' ||
    s === "'null'"
  ) {
    return undefined;
  }

  return s;
}

/**
 * Safe guard for revision data before passing to CKEditor
 * Prevents passing invalid strings that might be parsed as JSON
 * @param data - Revision data that might be HTML, undefined, or invalid string
 * @returns Clean data safe for CKEditor
 */
export function sanitizeRevisionData(data: unknown): string | undefined {
  // Handle object format from server
  if (data && typeof data === 'object' && 'content' in data) {
    return asHtmlString((data as any).content);
  }

  // Handle string format
  return asHtmlString(data);
}
